import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import _find from 'lodash/find';
import { Modal, Row, Col, Button, Form, Icon } from 'grab-ui';
import { isEmpty } from 'common/utils/data-utils';
import { GA_EVENT } from 'constants/ga-constants';
import { transformServiceTypes } from 'selectors/booking-selectors';
import { getUserCountryCode, getCompany } from 'helper/user';
import { fetchAddress } from 'helper/address-search';
import SelectField from 'components/form-field/select-field/select-field';
import cssSelect from 'components/form-field/select-field/select-field.less';
import generateExcel from 'common/utils/generate-excel';

import css from './download-template-modal.less';

class DownloadTemplateModal extends Component {
  constructor(props) {
    super(props);
    const { userInfo, city } = this.props;
    this.cityName = city.name;
    this.cityCode = city.code;
    this.cityPOI = city.poi;
    this.countryCode = getUserCountryCode(userInfo);
    this.selectedCityID = null;
    this.selectedCityServiceTypes = [];
    this.state = {
      isServiceUnavailable: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { city } = prevProps;
    const { visible, city: newCity } = this.props;
    if (!this.firstTimeLoadedDownloadModal && visible) {
      this.firstTimeLoadedDownloadModal = true;
      this.fetchSelectedCityTemplateData();
    } else if (!visible && this.firstTimeLoadedDownloadModal) {
      this.firstTimeLoadedDownloadModal = false;
    } else if (city.name !== newCity.name) {
      this.cityName = newCity.name;
      this.cityCode = newCity.code;
      this.cityPOI = newCity.poi;
    }
  }

  componentWillUnmount() {
    this.cityName = null;
    this.cityCode = null;
    this.cityPOI = null;
    this.countryCode = null;
    this.selectedCityServiceTypes = [];
    this.selectedCityID = null;
  }

  onHideCityModal = e => {
    e.preventDefault();
    const { hideModal } = this.props;
    hideModal();
  };

  onSelectCity = value => {
    const { cities } = this.props;
    const cityInfo = _find(cities, city => city.code === value);
    this.cityName = cityInfo.name;
    this.cityCode = cityInfo.code;
    this.cityPOI = cityInfo.poi;
    this.setState(
      {
        isServiceUnavailable: true,
      },
      () => {
        this.fetchSelectedCityTemplateData();
      },
    );
  };

  getFirstCityIDFromPOISearch = async () => {
    const { userInfo, city } = this.props;
    const { data } = await fetchAddress(
      this.cityPOI,
      city,
      getCompany(userInfo),
    );
    const { result } = data.resp;
    if (isEmpty(result)) return null;

    const mappedAddrsesWithCityName = result.filter(addressResult => {
      const cityResult = addressResult.address.city;
      if (!isEmpty(cityResult)) {
        if (cityResult.includes(this.cityName)) {
          return addressResult;
        }
      }
      return null;
    });

    return isEmpty(mappedAddrsesWithCityName)
      ? result[0].address.cityId
      : mappedAddrsesWithCityName[0].address.cityId;
  };

  downloadExcel = () => {
    const {
      isCashlessPaymentDisabled,
      isCashPaymentDisabled,
      payments,
      hideModal,
    } = this.props;
    if (
      isEmpty(this.selectedCityID) ||
      isEmpty(this.selectedCityServiceTypes)
    ) {
      return;
    }

    generateExcel({
      cityID: this.selectedCityID,
      serviceTypes: this.selectedCityServiceTypes,
      paymentMethods: { result: payments },
      countryCode: this.countryCode,
      cityName: this.cityName,
      cityCode: this.cityCode,
      isCashlessPaymentDisabled,
      isCashPaymentDisabled,
      callback: hideModal,
    });
  };

  handleDownloadExcel = e => {
    e.preventDefault();
    window.pushGAEvent({ event: GA_EVENT.DOWNLOAD_NEW_EXCEL_TEMPLATE });
    this.downloadExcel();
  };

  updateServiceAvailability = isAvailable => {
    this.setState({
      isServiceUnavailable: !isAvailable,
    });
  };

  fetchSelectedCityTemplateData = async () => {
    const { fetchServiceTypes } = this.props;
    const cityID = await this.getFirstCityIDFromPOISearch();
    if (isEmpty(cityID)) {
      this.updateServiceAvailability(false);
      return;
    }
    this.selectedCityID = cityID;

    const rawServiceTypes = await new Promise(resolve =>
      fetchServiceTypes(cityID, resolve),
    );
    if (isEmpty(rawServiceTypes) || isEmpty(rawServiceTypes.response)) {
      this.updateServiceAvailability(false);
      return;
    }

    const serviceTypes = transformServiceTypes(rawServiceTypes.response);
    if (!serviceTypes || isEmpty(serviceTypes)) {
      this.updateServiceAvailability(false);
      return;
    }

    this.selectedCityServiceTypes = serviceTypes;
    this.updateServiceAvailability(true);
  };

  renderServiceUnableError = isServiceUnavailable =>
    isServiceUnavailable ? (
      <div className={css.serviceUnableContainer}>
        <span className={css.serviceUnable}>
          {I18n.t('booking.bulkUploadExcel.downloadModal.serviceUnable')}
        </span>
      </div>
    ) : null;

  renderCitiesOptions = () => {
    const { cities } = this.props;
    if (!isEmpty(cities)) {
      return cities.map(city => {
        const { code, name } = city;
        return {
          value: code,
          child: <div>{name}</div>,
        };
      });
    }
  };

  render() {
    const { visible, onCancel, form } = this.props;
    const { isServiceUnavailable } = this.state;
    const citiesOptions = this.renderCitiesOptions();
    return (
      <Modal
        visible={visible}
        className={css.selectCityModal}
        onCancel={onCancel}
        closable={false}
        maskClosable
        footer={[
          <div className={css.footerContainer} key="buttons">
            <div key="keepOrder">
              <Button
                key="download"
                onClick={this.handleDownloadExcel}
                type="primary"
                className={css.downloadButton}
                disabled={isServiceUnavailable}
              >
                <span>
                  {I18n.t(
                    'booking.bulkUploadExcel.downloadModal.downloadButton',
                  )}
                </span>
              </Button>
            </div>
          </div>,
        ]}
        centered
        width={750}
      >
        <div
          onClick={this.onHideCityModal}
          role="button"
          tabIndex="0"
          onKeyDown={null}
          className={css.backArrow}
        >
          <Icon type="left" />
        </div>
        <Row type="flex">
          <Col span={5} />
          <Col span={15} className={css.modalSelectCityContent}>
            <p className={css.headingCity}>
              {I18n.t('booking.bulkUploadExcel.downloadModal.heading')}
            </p>
            <p className={css.subInfo}>
              {I18n.t('booking.bulkUploadExcel.downloadModal.subInfo')}
            </p>
            <div className={css.selectContent}>
              <p>
                <span className={css.labelSelect}>City</span>
              </p>
              <Form>
                <SelectField
                  id="city"
                  options={citiesOptions}
                  form={form}
                  initialValue={this.cityName}
                  onSelect={this.onSelectCity}
                  className={
                    isServiceUnavailable ? cssSelect.errorSelect : null
                  }
                />
                {this.renderServiceUnableError(isServiceUnavailable)}
              </Form>
            </div>
          </Col>
        </Row>
      </Modal>
    );
  }
}

DownloadTemplateModal.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  fetchServiceTypes: PropTypes.func.isRequired,
  // history: shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  cities: PropTypes.arrayOf(PropTypes.shape({})),
  city: PropTypes.shape({}).isRequired,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
  isCashPaymentDisabled: PropTypes.bool.isRequired,
  payments: PropTypes.arrayOf(PropTypes.shape({})),
};

DownloadTemplateModal.defaultProps = {
  cities: [],
  visible: false,
  payments: [],
};

export default Form.create()(DownloadTemplateModal);
